/**
 * Phase N.1 (2026-08-21) — Bulk statement actions bar + confirm/result modals.
 *
 * Exports:
 *   * ``BulkActionsBar``   — sticky footer bar rendered when N > 0 rows
 *     are selected on the statements list. Emits ``onAction("send" |
 *     "charge" | "void")`` when a button is clicked. Greys out buttons
 *     whose action has zero eligible selected rows.
 *   * ``BulkConfirmModal`` — opens per action. Shows per-row
 *     eligibility summary and (for void) a reason + notes field.
 *   * ``BulkResultModal``  — shows the succeeded/failed/skipped
 *     summary after the batch API returns.
 *
 * The parent owns:
 *   * the ``selectedIds`` state (Set<string>)
 *   * the current action being confirmed (``pendingAction``)
 *   * calling ``api.post("/admin/statements/bulk-{action}", …)`` and
 *     surfacing the returned result envelope to ``BulkResultModal``
 *
 * Eligibility rules (kept in sync with ``services.bulk_statements``):
 *   * send:   only ``draft``
 *   * charge: only ``sent`` or ``failed``
 *   * void:   ``draft`` / ``sent`` / ``failed`` (not ``paid`` / ``processing`` / ``void``)
 */
import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Send, CreditCard, Ban, X, CheckCircle2, AlertTriangle } from "lucide-react";


function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    return `$${(Math.abs(cents) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}


const ELIGIBLE = {
    send:   (status) => status === "draft",
    charge: (status) => status === "sent" || status === "failed",
    void:   (status) => ["draft", "sent", "failed"].includes(status),
};


function splitEligible(rows, action) {
    const eligible = [];
    const skipped = [];
    for (const r of rows) {
        if (ELIGIBLE[action](r.status)) {
            eligible.push(r);
        } else {
            skipped.push({
                ...r,
                skip_reason: skipReason(action, r.status),
            });
        }
    }
    return { eligible, skipped };
}


function skipReason(action, status) {
    if (action === "send") {
        if (status === "sent")       return "Already sent";
        if (status === "paid")       return "Already paid";
        if (status === "void")       return "Voided";
        if (status === "processing") return "Being charged";
        return "Not a draft";
    }
    if (action === "charge") {
        if (status === "draft")      return "Not sent yet";
        if (status === "paid")       return "Already paid";
        if (status === "void")       return "Voided";
        if (status === "processing") return "Already charging";
        return "Wrong status to charge";
    }
    if (action === "void") {
        if (status === "paid")       return "Paid — refund via K.4 instead";
        if (status === "processing") return "Being charged";
        if (status === "void")       return "Already voided";
    }
    return "Not eligible";
}


// -----------------------------------------------------------------------
// BulkActionsBar
// -----------------------------------------------------------------------


export function BulkActionsBar({ selectedRows, onAction, onClear }) {
    const count = selectedRows.length;
    // Hooks MUST be called before any early return (rules of hooks).
    const totals = useMemo(
        () => selectedRows.reduce((a, r) => a + (r.total_cents || 0), 0),
        [selectedRows],
    );
    if (count === 0) return null;

    const eligibleSend   = selectedRows.some((r) => ELIGIBLE.send(r.status));
    const eligibleCharge = selectedRows.some((r) => ELIGIBLE.charge(r.status));
    const eligibleVoid   = selectedRows.some((r) => ELIGIBLE.void(r.status));

    return (
        <div
            className="sticky bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-300 shadow-lg px-4 py-3 flex items-center gap-3"
            data-testid="bulk-actions-bar"
        >
            <div className="text-sm font-medium text-slate-700">
                <span data-testid="bulk-actions-count">
                    {count} selected
                </span>
                <span className="text-slate-500 ml-2">
                    · {fmtMoney(totals)} total
                </span>
            </div>
            <div className="flex-1" />
            <Button
                variant="outline" size="sm"
                onClick={() => onAction("send")}
                disabled={!eligibleSend}
                data-testid="bulk-action-send-btn"
            >
                <Send size={14} className="mr-1.5" /> Send
            </Button>
            <Button
                variant="outline" size="sm"
                onClick={() => onAction("charge")}
                disabled={!eligibleCharge}
                data-testid="bulk-action-charge-btn"
                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
            >
                <CreditCard size={14} className="mr-1.5" /> Charge
            </Button>
            <Button
                variant="outline" size="sm"
                onClick={() => onAction("void")}
                disabled={!eligibleVoid}
                data-testid="bulk-action-void-btn"
                className="border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
                <Ban size={14} className="mr-1.5" /> Void
            </Button>
            <Button
                variant="ghost" size="sm"
                onClick={onClear}
                data-testid="bulk-action-clear-btn"
            >
                <X size={14} className="mr-1.5" /> Clear
            </Button>
        </div>
    );
}


// -----------------------------------------------------------------------
// BulkConfirmModal
// -----------------------------------------------------------------------


export function BulkConfirmModal({
    open, action, selectedRows, busy, onCancel, onConfirm,
}) {
    const [voidReason, setVoidReason] = useState("");
    const [sendReason, setSendReason] = useState("Batch send from statements list");

    const { eligible, skipped } = useMemo(
        () => splitEligible(selectedRows, action || "send"),
        [selectedRows, action],
    );
    const eligibleTotal = eligible.reduce(
        (a, r) => a + (r.total_cents || 0), 0,
    );

    if (!open || !action) return null;

    const isVoid = action === "void";
    const isDestructive = action === "void" || action === "charge";
    const label = action.charAt(0).toUpperCase() + action.slice(1);

    const reasonValid = !isVoid || voidReason.trim().length >= 5;
    const sendValid = action !== "send" || sendReason.trim().length >= 5;
    const canConfirm = !busy && eligible.length > 0
        && reasonValid && sendValid;

    function submit() {
        if (!canConfirm) return;
        const ids = eligible.map((r) => r.id);
        if (action === "send") {
            onConfirm({ statement_ids: ids, send_reason: sendReason.trim() });
        } else if (action === "charge") {
            onConfirm({ statement_ids: ids });
        } else if (action === "void") {
            onConfirm({ statement_ids: ids, void_reason: voidReason.trim() });
        }
    }

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
            <DialogContent
                className="max-w-xl"
                data-testid="bulk-confirm-modal"
            >
                <DialogHeader>
                    <DialogTitle>
                        {label} {eligible.length}{" "}
                        statement{eligible.length === 1 ? "" : "s"}?
                    </DialogTitle>
                    <DialogDescription data-testid="bulk-confirm-summary">
                        You are about to {action} {eligible.length}{" "}
                        statement{eligible.length === 1 ? "" : "s"}{" "}
                        totaling {fmtMoney(eligibleTotal)}.
                        {skipped.length > 0 && (
                            <> {skipped.length} selected statement
                            {skipped.length === 1 ? " is" : "s are"} not
                            eligible and will be skipped.</>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {skipped.length > 0 && (
                    <div
                        className="rounded-md border border-amber-300 bg-amber-50 p-3 max-h-40 overflow-y-auto"
                        data-testid="bulk-confirm-skipped-list"
                    >
                        <div className="text-xs font-semibold text-amber-900 mb-1.5">
                            Skipped ({skipped.length}):
                        </div>
                        <ul className="text-xs text-amber-900 space-y-1">
                            {skipped.map((r) => (
                                <li key={r.id}
                                    data-testid={`bulk-confirm-skipped-${r.id}`}>
                                    <span className="font-mono">
                                        {r.id.slice(0, 10)}…
                                    </span>{" "}
                                    — {r.skip_reason}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {action === "send" && (
                    <div className="space-y-1.5">
                        <Label htmlFor="bulk-send-reason">
                            Send reason
                        </Label>
                        <Textarea
                            id="bulk-send-reason"
                            data-testid="bulk-send-reason-input"
                            rows={2}
                            value={sendReason}
                            onChange={(e) => setSendReason(e.target.value)}
                        />
                    </div>
                )}
                {isVoid && (
                    <div className="space-y-1.5">
                        <Label htmlFor="bulk-void-reason">
                            Void reason <span className="text-red-600">*</span>
                        </Label>
                        <Textarea
                            id="bulk-void-reason"
                            data-testid="bulk-void-reason-input"
                            rows={2}
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            placeholder="Why are these being voided? (min 5 chars)"
                        />
                        {voidReason.trim() && voidReason.trim().length < 5 && (
                            <p className="text-xs text-red-600">
                                Reason must be at least 5 characters.
                            </p>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onCancel}
                        disabled={busy}
                        data-testid="bulk-confirm-cancel-btn"
                    >
                        Keep selection
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!canConfirm}
                        className={isDestructive
                            ? "bg-red-600 hover:bg-red-700 text-white"
                            : ""}
                        data-testid="bulk-confirm-submit-btn"
                    >
                        {busy
                            ? "Working…"
                            : `Confirm ${label}`
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// -----------------------------------------------------------------------
// BulkResultModal
// -----------------------------------------------------------------------


export function BulkResultModal({ open, result, onClose }) {
    if (!open || !result) return null;
    const succeeded = result.succeeded ?? 0;
    const failed    = result.failed ?? 0;
    const skipped   = result.skipped ?? 0;
    const failedRows  = (result.results || []).filter((r) => r.status === "failed");
    const skippedRows = (result.results || []).filter((r) => r.status === "skipped");

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent
                className="max-w-xl"
                data-testid="bulk-result-modal"
            >
                <DialogHeader>
                    <DialogTitle>Bulk {result.action} complete</DialogTitle>
                    <DialogDescription>
                        <span data-testid="bulk-result-summary">
                            {succeeded} succeeded · {failed} failed · {skipped} skipped
                        </span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    {succeeded > 0 && (
                        <div
                            className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 flex gap-2"
                            data-testid="bulk-result-succeeded"
                        >
                            <CheckCircle2 size={16} className="mt-0.5 flex-none" />
                            <div>
                                <span className="font-semibold">{succeeded}</span>{" "}
                                {result.action === "send" && "sent"}
                                {result.action === "charge" && "charged"}
                                {result.action === "void" && "voided"}
                                {" "}successfully.
                            </div>
                        </div>
                    )}
                    {failedRows.length > 0 && (
                        <div
                            className="rounded-md border border-red-200 bg-red-50 p-3 max-h-40 overflow-y-auto"
                            data-testid="bulk-result-failed-list"
                        >
                            <div className="text-xs font-semibold text-red-900 mb-1.5 flex items-center gap-1.5">
                                <AlertTriangle size={14} />
                                Failed ({failedRows.length}):
                            </div>
                            <ul className="text-xs text-red-900 space-y-1">
                                {failedRows.map((r) => (
                                    <li key={r.statement_id}
                                        data-testid={`bulk-result-failed-${r.statement_id}`}>
                                        <span className="font-mono">
                                            {r.statement_id.slice(0, 10)}…
                                        </span>{" "}
                                        — {r.error_message || r.error_code}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {skippedRows.length > 0 && (
                        <div
                            className="rounded-md border border-amber-200 bg-amber-50 p-3 max-h-32 overflow-y-auto"
                            data-testid="bulk-result-skipped-list"
                        >
                            <div className="text-xs font-semibold text-amber-900 mb-1.5">
                                Skipped ({skippedRows.length}):
                            </div>
                            <ul className="text-xs text-amber-900 space-y-1">
                                {skippedRows.map((r) => (
                                    <li key={r.statement_id}>
                                        <span className="font-mono">
                                            {r.statement_id.slice(0, 10)}…
                                        </span>{" "}
                                        — {r.reason || r.reason_code}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={onClose} data-testid="bulk-result-close-btn">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
